import { Linking, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Box, Divider, HStack, IconButton, Image, Popover, Pressable, useTheme, VStack } from 'native-base';
import moment from 'moment';

import { ArrowSquareUpRightIcon, ClockRefreshIcon, InfoCircleIcon, Text } from '@pimm/base';
import { SyncInfoDto } from '@pimm/services/lib/sms-workforce';
import { useSiteTime } from '@app/features/store-core';
import { useTransition } from 'react';

type ImporterConnectionProps = {
  syncInfo?: SyncInfoDto;
};

export const ImporterConnection = ({ syncInfo }: ImporterConnectionProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const siteTime = useSiteTime();
  const isOffline = syncInfo?.status?.toLowerCase() !== 'online';
  const isImporterEnabled = syncInfo?.status?.toLowerCase() !== 'not enabled';
  const importerStatus = `${t(
    isImporterEnabled ? (isOffline ? 'sales_tracker:offline' : 'sales_tracker:online') : 'sales_tracker:not_enabled',
  )}`;
  let lastImportDateTime = syncInfo?.lastDataSyncUtc;

  const lastImportDateTimeText = syncInfo?.lastDataSyncUtc
    ? moment
        .utc(syncInfo.lastDataSyncUtc)
        .utcOffset(siteTime.utcOffsetInMinutes ?? 0)
        .format('DD/MM/YYYY h:mm A')
    : '--';

  const handleOpenBlankPage = () => {
    if (syncInfo?.vendorInfo?.vendorPortalUrl) {
      if (Platform.OS === 'web') window.open(syncInfo?.vendorInfo?.vendorPortalUrl, '_blank', 'noreferrer');
      else Linking.openURL(syncInfo?.vendorInfo?.vendorPortalUrl);
    }
  };

  return (
    <HStack space={2} alignItems="center" rounded="lg" py={0.5} pl={1} pr={2} h={10} borderWidth={1} borderColor="gray.300">
      <Pressable onPress={handleOpenBlankPage}>
        <HStack space={2} alignItems="center" pl={1.5}>
          <Box w={60} h={9}>
            <Image
              source={{
                uri: syncInfo?.vendorInfo?.logoImgUrl,
              }}
              style={{ flex: 1, width: '100%', height: '100%', resizeMode: 'contain' }}
              alt={syncInfo?.vendorInfo?.vendorPortalUrl ?? syncInfo?.vendorInfo?.fullName ?? 'Vendor Logo'}
            />
          </Box>
          <ArrowSquareUpRightIcon size="md" color={colors.gray[700]} />
        </HStack>
      </Pressable>

      <Divider orientation="vertical" height="80%" bg="gray.300" />

      <Box minW="60px">
        <VStack>
          <HStack>
            <Box flex="none" rounded="lg" px={1} bg={isOffline ? 'error.50' : 'success.50'}>
              <Text size="xs" fontWeight={500} color={isOffline ? 'error.600' : 'success.600'}>
                {t('sales_tracker:status')}: {importerStatus}
              </Text>
            </Box>
          </HStack>
          {isImporterEnabled && !!lastImportDateTime && (
            <HStack space={1} alignItems="center">
              <ClockRefreshIcon size={4} color={colors.gray[700]} />
              <Text size="sm" fontWeight={700} color="gray.700">
                {lastImportDateTimeText}
              </Text>
            </HStack>
          )}
        </VStack>
      </Box>

      <Popover
        isKeyboardDismissable
        trigger={popoverProps => {
          return (
            <IconButton
              mx={0.5}
              p={0}
              _hover={{ bg: 'transparent' }}
              icon={<InfoCircleIcon size={4} color={colors.gray[700]} />}
              {...popoverProps}
            />
          );
        }}
      >
        <Popover.Content accessibilityLabel={t('sales_tracker:api_service')} rounded="lg">
          <Popover.Arrow zIndex={1} />
          <Popover.Body p={3} w="280">
            <VStack space={2}>
              <HStack>
                <Box rounded="lg" py={0.5} px={1.5} bg={isImporterEnabled ? (isOffline ? 'error.50' : 'success.50') : 'gray.50'}>
                  <Text size="md" fontWeight={500} color={isImporterEnabled ? (isOffline ? 'error.600' : 'success.600') : 'gray.600'}>
                    {t('sales_tracker:api_service')}: {importerStatus}
                  </Text>
                </Box>
              </HStack>

              <HStack>
                <Box pt={0.5} pr={2}>
                  <ClockRefreshIcon size="18px" color={colors.gray[700]} />
                </Box>
                <VStack>
                  <Text size="sm" fontWeight={600} color="gray.700">
                    {t('sales_tracker:last_data_sync')}
                  </Text>
                  <Text size="sm" fontWeight={700} color="black">
                    {isImporterEnabled && !!lastImportDateTime ? lastImportDateTimeText : '--'}
                  </Text>
                </VStack>
              </HStack>

              <Box>
                <Text size="sm" fontWeight={700} color="black">
                  {`${t('sales_tracker:note')}:`}
                </Text>

                <Text size="sm" fontWeight={600} color="black" lineHeight="xs">
                  {`${t('sales_tracker:data_sync')}`}
                </Text>
              </Box>
            </VStack>
          </Popover.Body>
        </Popover.Content>
      </Popover>
    </HStack>
  );
};
